import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import MusaCard from "@/components/MusaCard";
import InstallAppButton from "@/components/InstallAppButton";
import { Crown, Shield, Sparkles, Star, MapPin, ArrowRight } from "lucide-react";

// Curated fallback portraits of women (sourced from Pexels — public, royalty-free).
// Used ONLY when no verified Musa photos are available yet.
const FALLBACK_WOMEN_PHOTOS = [
  "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/1689731/pexels-photo-1689731.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/1499327/pexels-photo-1499327.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/1391498/pexels-photo-1391498.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/1126993/pexels-photo-1126993.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/2613260/pexels-photo-2613260.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/1832959/pexels-photo-1832959.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/2613257/pexels-photo-2613257.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/2787341/pexels-photo-2787341.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/1382731/pexels-photo-1382731.jpeg?auto=compress&cs=tinysrgb&w=1600",
];
const ROTATE_MS = 20_000; // 20 seconds per photo

export default function Home() {
  const { t } = useTranslation();
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [cities, setCities] = useState([]);
  const [stats, setStats] = useState({ total: 0, vip: 0, cities: 0 });
  const [bgPhotos, setBgPhotos] = useState([]);
  const [bgIdx, setBgIdx] = useState(0);

  useEffect(() => {
    api.get("/profiles/featured").then((r) => setFeatured(r.data || []));
    api.get("/profiles", { params: { limit: 12 } }).then((r) => {
      setRecent(r.data?.items || []);
      setStats((s) => ({ ...s, total: r.data?.total || 0 }));
    });
    // Rotating background: ONLY verified profiles. Fallback to a curated
    // women-only photo set when there are none yet.
    api
      .get("/profiles", { params: { limit: 60, verified_only: true } })
      .then((r) => {
        const items = r.data?.items || [];
        const photos = items
          .map((p) => p.hero_photo || (Array.isArray(p.photos) && p.photos[0]) || "")
          .filter(Boolean);
        const final = photos.length > 0 ? photos : [...FALLBACK_WOMEN_PHOTOS];
        // Shuffle so different visitors see a different first image
        final.sort(() => Math.random() - 0.5);
        setBgPhotos(final);
      })
      .catch(() => setBgPhotos([...FALLBACK_WOMEN_PHOTOS]));
    api.get("/cities").then((r) => {
      setCities(r.data || []);
      setStats((s) => ({ ...s, cities: (r.data || []).length }));
    });
  }, []);

  // Rotate hero background every minute
  useEffect(() => {
    if (bgPhotos.length <= 1) return;
    // Preload the next image so the swap is smooth
    const preload = (src) => { const i = new Image(); i.src = src; };
    preload(bgPhotos[(bgIdx + 1) % bgPhotos.length]);
    const t = setInterval(() => {
      setBgIdx((i) => (i + 1) % bgPhotos.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [bgPhotos, bgIdx]);

  return (
    <div className="relative">
      {/* HERO — luxe split with portrait, big serif and a marquee tag bar */}
      <section className="relative overflow-hidden" data-testid="hero-section" style={{ minHeight: "70vh" }}>
        {/* Rotating background slideshow — one frame per Musa, 60s each, crossfade */}
        <div className="absolute inset-0" data-testid="hero-bg-slideshow">
          {bgPhotos.length > 0 ? (
            bgPhotos.map((src, i) => (
              <div
                key={src + i}
                className="absolute inset-0"
                style={{
                  backgroundImage: `url('${src}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center 25%",
                  filter: "saturate(0.85) contrast(1.05)",
                  opacity: i === bgIdx ? 1 : 0,
                  transition: "opacity 1.6s ease-in-out",
                  // Slow Ken-Burns zoom on the active slide
                  transform: i === bgIdx ? "scale(1.06)" : "scale(1)",
                  transformOrigin: "center 30%",
                  transitionProperty: "opacity, transform",
                  transitionDuration: "1.6s, 20s",
                  transitionTimingFunction: "ease-in-out, linear",
                }}
                data-testid={`hero-bg-${i}`}
              />
            ))
          ) : (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url('${FALLBACK_WOMEN_PHOTOS[0]}')`,
                backgroundSize: "cover",
                backgroundPosition: "center 30%",
                filter: "saturate(0.85) contrast(1.05)",
              }}
            />
          )}
        </div>
        {/* Heavy red+black gradient for premium adult vibe */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(115deg, rgba(8,7,8,0.97) 0%, rgba(8,7,8,0.78) 35%, rgba(127,29,29,0.55) 70%, rgba(200,16,46,0.45) 100%)",
          }}
        />
        {/* Subtle grain on top */}
        <div className="absolute inset-0 grain pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-8 flex flex-col gap-6" style={{ minHeight: "70vh", justifyContent: "center" }}>
          <div className="flex items-center gap-3 fade-up">
            <span style={{ width: 40, height: 1, background: "var(--brand-primary)" }} />
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: "var(--brand-primary)" }} data-testid="hero-overline">
              {t("home.overline")}
            </span>
          </div>

          <h1
            className="font-serif font-light tracking-tight fade-up"
            style={{
              color: "var(--text-primary)",
              fontSize: "clamp(2.6rem, 8vw, 6rem)",
              lineHeight: 0.98,
              animationDelay: "80ms",
              textShadow: "0 4px 24px rgba(0,0,0,0.6)",
            }}
            data-testid="hero-title"
          >
            {t("home.title_line1")}<br />
            {t("home.title_line2_pre")} <em style={{ color: "var(--brand-primary)", fontStyle: "italic", fontWeight: 600 }}>{t("home.title_line2_em")}</em>{t("home.title_line2_post")}<br />
            <span style={{ color: "var(--vip-gold)", fontStyle: "italic" }}>{t("home.title_line3")}</span>.
          </h1>

          <p
            className="max-w-2xl fade-up"
            style={{ color: "var(--text-primary)", fontSize: "1.15rem", lineHeight: 1.7, animationDelay: "180ms" }}
          >
            <strong style={{ color: "var(--brand-primary)" }}>{t("home.subtitle_pre")}</strong> {t("home.subtitle_post")}
            <br />
            <span style={{ color: "var(--text-secondary)" }}>{t("home.subtitle_2")}</span>
          </p>

          <div className="flex flex-wrap gap-3 fade-up" style={{ animationDelay: "260ms" }}>
            <Link to="/galeria" className="btn-primary text-sm" data-testid="hero-cta-explore" style={{ padding: "1rem 1.8rem" }}>
              {t("home.cta_explore")} <ArrowRight size={14} className="inline ml-2" />
            </Link>
            <Link to="/galeria?vip=1" className="btn-secondary text-sm" data-testid="hero-cta-vip" style={{ padding: "1rem 1.8rem", borderColor: "var(--vip-gold)", color: "var(--vip-gold)" }}>
              <Crown size={14} strokeWidth={1.5} /> {t("home.cta_vip")}
            </Link>
            <Link to="/registro?role=companion" className="btn-ghost text-sm" data-testid="hero-cta-publish" style={{ padding: "1rem 1.8rem" }}>
              {t("home.cta_publish")}
            </Link>
            <InstallAppButton variant="secondary" />
          </div>

          {/* Stats strip */}
          <div className="flex flex-wrap gap-8 mt-6 pt-6 fade-up" style={{ borderTop: "1px solid rgba(245,245,244,0.15)", animationDelay: "340ms" }}>
            <Stat icon={<Sparkles size={18} strokeWidth={1.3} />} label={t("home.stat_active")} value={`${stats.total || "500"}+`} />
            <Stat icon={<Crown size={18} strokeWidth={1.3} />} label={t("home.stat_vip_accounts")} value="100+" />
            <Stat icon={<MapPin size={18} strokeWidth={1.3} />} label={t("home.stat_cities")} value={`${stats.cities || 8}`} />
            <Stat icon={<Shield size={18} strokeWidth={1.3} />} label={t("home.stat_verification")} value={t("home.stat_verification_value")} />
          </div>
        </div>

        {/* Marquee bar at bottom of hero */}
        <div
          className="absolute bottom-0 left-0 right-0 overflow-hidden"
          style={{
            background: "rgba(200,16,46,0.95)",
            borderTop: "1px solid var(--vip-gold)",
            borderBottom: "1px solid var(--vip-gold)",
            color: "#fff",
            padding: "0.7rem 0",
          }}
          data-testid="hero-marquee"
        >
          <div
            className="flex items-center gap-12 whitespace-nowrap"
            style={{
              animation: "pk2-marquee 30s linear infinite",
              fontFamily: "Cormorant Garamond, serif",
              fontSize: "1.1rem",
              letterSpacing: "0.08em",
            }}
          >
            {Array.from({ length: 4 }).map((_, k) => (
              <React.Fragment key={k}>
                <span>{t("home.marquee_independent")}</span>
                <span>{t("home.marquee_agencies")}</span>
                <span>{t("home.marquee_milf")}</span>
                <span>{t("home.marquee_uni")}</span>
                <span>{t("home.marquee_curvy")}</span>
                <span>{t("home.marquee_trans")}</span>
                <span>{t("home.marquee_models")}</span>
                <span>{t("home.marquee_couple")}</span>
                <span>{t("home.marquee_hotel")}</span>
                <span>{t("home.marquee_discreet")}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
        <style>{`@keyframes pk2-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
      </section>

      {/* CITIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="cities-section">
        <div className="flex flex-col md:flex-row justify-between md:items-end gap-4 mb-8">
          <div>
            <div className="overline mb-3">{t("home.cities_overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light" style={{ color: "var(--text-primary)" }}>
              {t("home.cities_title_pre")} <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>{t("home.cities_title_em")}</em>
            </h2>
          </div>
          <Link to="/galeria" className="btn-ghost text-sm" data-testid="cities-view-all">{t("home.cities_view_all")}</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cities.map((c) => (
            <Link
              key={c.slug}
              to={`/galeria?city=${encodeURIComponent(c.city)}`}
              className="group relative p-6 flex flex-col gap-1 transition-all"
              style={{
                background: "var(--bg-surface)",
                border: "1px solid var(--border-subtle)",
              }}
              data-testid={`city-link-${c.slug}`}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--brand-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border-subtle)")}
            >
              <span className="overline">{c.state}</span>
              <span className="font-serif text-2xl" style={{ color: "var(--text-primary)" }}>{c.city}</span>
              <span className="text-xs mt-2" style={{ color: "var(--brand-primary)" }}>{t("home.cities_enter")}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED VIP */}
      {featured.length > 0 && (
        <section
          className="py-14 md:py-20 relative"
          style={{
            background: "linear-gradient(180deg, var(--bg-base) 0%, #100d0a 100%)",
            borderTop: "1px solid var(--border-subtle)",
            borderBottom: "1px solid var(--border-subtle)",
          }}
          data-testid="featured-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col items-center text-center gap-3 mb-10">
              <div className="vip-badge"><Crown size={12} strokeWidth={1.5} /> {t("home.featured_badge")}</div>
              <h2 className="font-serif text-4xl md:text-6xl font-light" style={{ color: "var(--text-primary)" }}>
                {t("home.featured_title_pre")} <em style={{ color: "var(--vip-gold)", fontStyle: "italic" }}>{t("home.featured_title_em")}</em> {t("home.featured_title_post")}
              </h2>
              <p className="max-w-xl text-sm" style={{ color: "var(--text-secondary)" }}>
                {t("home.featured_subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {featured.slice(0, 8).map((p, i) => (
                <MusaCard key={p.id} profile={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RECENT */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="recent-section">
        <div className="flex flex-col md:flex-row justify-between md:items-end gap-4 mb-8">
          <div>
            <div className="overline mb-3">{t("home.recent_overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light" style={{ color: "var(--text-primary)" }}>
              {t("home.recent_title_pre")} <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>{t("home.recent_title_em")}</em>
            </h2>
          </div>
          <Link to="/galeria" className="btn-secondary" data-testid="recent-view-all">{t("home.recent_view_all")}</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {recent.map((p, i) => (
            <MusaCard key={p.id} profile={p} index={i} />
          ))}
        </div>
      </section>

      {/* JOIN CTA — for escorts/companions */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(200,16,46,0.25), transparent 60%), radial-gradient(circle at 80% 80%, rgba(212,175,55,0.18), transparent 55%), linear-gradient(135deg, #1C1A18 0%, #080708 100%)",
        }}
        data-testid="join-cta-section"
      >
        <div
          className="absolute inset-0 opacity-15 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(200,16,46,0.04) 0 1px, transparent 1px 14px)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center flex flex-col items-center gap-6">
          <div className="vip-badge"><Crown size={12} strokeWidth={1.5} /> {t("home.join_badge")}</div>
          <h2 className="font-serif font-light leading-tight" style={{ color: "var(--text-primary)", fontSize: "clamp(2.5rem, 6vw, 5rem)" }}>
            {t("home.join_title_1")}<br />
            <em style={{ color: "var(--vip-gold)", fontStyle: "italic" }}>{t("home.join_title_2")}</em>
          </h2>
          <p className="max-w-2xl text-base md:text-lg" style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
            {t("home.join_subtitle_pre")} <strong style={{ color: "var(--text-primary)" }}>{t("home.join_subtitle_free")}</strong>{t("home.join_subtitle_mid")} <strong style={{ color: "var(--vip-gold)" }}>{t("home.join_subtitle_15")}</strong>{t("home.join_subtitle_post")}
          </p>
          <div className="flex flex-wrap gap-3 mt-4 justify-center">
            <Link to="/registro?role=companion" className="btn-primary" data-testid="join-cta-button" style={{ padding: "1rem 1.8rem" }}>
              {t("home.join_cta_publish")} <ArrowRight size={14} className="inline ml-2" />
            </Link>
            <Link to="/registro?role=agency" className="btn-secondary" data-testid="join-cta-agency" style={{ padding: "1rem 1.8rem" }}>
              {t("home.join_cta_agency")}
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-8 pt-8" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <Perk>{t("home.perk_no_fees")}</Perk>
            <Perk>{t("home.perk_boost")}</Perk>
            <Perk>{t("home.perk_verified")}</Perk>
            <Perk>{t("home.perk_chat")}</Perk>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3" data-testid={`hero-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span style={{ color: "var(--brand-primary)" }}>{icon}</span>
      <div>
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{label}</div>
        <div className="font-serif text-xl" style={{ color: "var(--text-primary)" }}>{value}</div>
      </div>
    </div>
  );
}

function Perk({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs" style={{ color: "var(--text-secondary)" }}>
      <Star size={10} strokeWidth={1.5} color="var(--vip-gold)" /> {children}
    </span>
  );
}
